import React, { useState, useEffect } from 'react';
import { getPolicies, createPolicy, deletePolicy, getIdpConfigs } from '../mocks/api';

export default function Policies() {
  const [policies, setPolicies] = useState([]);
  const [idpConfigs, setIdpConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    sourceIdpId: '',
    destIdpId: '',
    userFilter: '',
    appsFilter: '',
    syncFrequency: 'daily',
  });

  const load = async () => {
    setLoading(true);
    const [pols, configs] = await Promise.all([getPolicies(), getIdpConfigs()]);
    setPolicies(pols);
    setIdpConfigs(configs);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const sourceIdps = idpConfigs.filter(c => c.type === 'source');
  const destIdps = idpConfigs.filter(c => c.type === 'destination');

  const getIdpName = (id) => idpConfigs.find(c => c.id === id)?.name || 'Unknown';

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    await createPolicy(form);
    setForm({ name: '', sourceIdpId: '', destIdpId: '', userFilter: '', appsFilter: '', syncFrequency: 'daily' });
    setShowForm(false);
    setSaving(false);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this policy?')) return;
    await deletePolicy(id);
    load();
  };

  if (loading) return <div className="loading">Loading policies...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <h2>Migration Policies</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ New Policy'}
        </button>
      </div>

      {showForm && (
        <div className="card form-card">
          <h3>Create New Policy</h3>
          <form onSubmit={handleCreate}>
            <div className="form-group">
              <label>Policy Name</label>
              <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Migrate Engineering Users" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Source IdP</label>
                <select required value={form.sourceIdpId} onChange={e => setForm({ ...form, sourceIdpId: e.target.value })}>
                  <option value="">Select source...</option>
                  {sourceIdps.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Destination IdP</label>
                <select required value={form.destIdpId} onChange={e => setForm({ ...form, destIdpId: e.target.value })}>
                  <option value="">Select destination...</option>
                  {destIdps.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>User Filter</label>
                <input value={form.userFilter} onChange={e => setForm({ ...form, userFilter: e.target.value })} placeholder="e.g. department=Engineering, role=admin" />
              </div>
              <div className="form-group">
                <label>Apps Filter</label>
                <input value={form.appsFilter} onChange={e => setForm({ ...form, appsFilter: e.target.value })} placeholder="e.g. type=SAML, all" />
              </div>
            </div>
            <div className="form-group">
              <label>Sync Frequency</label>
              <select value={form.syncFrequency} onChange={e => setForm({ ...form, syncFrequency: e.target.value })}>
                <option value="manual">Manual</option>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Creating...' : 'Create Policy'}
            </button>
          </form>
        </div>
      )}

      {policies.length === 0 ? (
        <div className="card"><p className="empty-state">No policies created yet.</p></div>
      ) : (
        <div className="policies-list">
          {policies.map(p => (
            <div key={p.id} className="card policy-card">
              <div className="policy-header">
                <h3>{p.name}</h3>
                <div className="policy-actions">
                  <span className={`badge badge-${p.status}`}>{p.status}</span>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(p.id)}>Delete</button>
                </div>
              </div>
              <div className="policy-details">
                <div className="policy-detail">
                  <span className="label">Source:</span>
                  <span>{getIdpName(p.sourceIdpId)}</span>
                </div>
                <div className="policy-detail">
                  <span className="label">Destination:</span>
                  <span>{getIdpName(p.destIdpId)}</span>
                </div>
                <div className="policy-detail">
                  <span className="label">User Filter:</span>
                  <span className="code">{p.userFilter || 'None'}</span>
                </div>
                <div className="policy-detail">
                  <span className="label">Apps Filter:</span>
                  <span className="code">{p.appsFilter || 'None'}</span>
                </div>
                <div className="policy-detail">
                  <span className="label">Sync:</span>
                  <span>{p.syncFrequency}</span>
                </div>
                <div className="policy-detail">
                  <span className="label">Created:</span>
                  <span>{p.createdAt}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
